(() => {
  'use strict';

  // A partial Fisher-Yates shuffle: work and memory depend on the card count.
  function sampleIndices(total, count) {
    const swaps = new Map();
    const selected = [];
    for (let remaining = total; selected.length < count; remaining--) {
      const pick = Math.floor(Math.random() * remaining);
      selected.push(swaps.get(pick) ?? pick);
      swaps.set(pick, swaps.get(remaining - 1) ?? remaining - 1);
    }
    return selected;
  }

  class ApexFeaturedDesigns extends HTMLElement {
    connectedCallback() {
      if (this.initialized || this.dataset.randomize !== 'true' || window.Shopify?.designMode) return;
      this.initialized = true;
      // Keep cards stable as soon as someone starts using them.
      this.onInteraction = () => { this.interacted = true; this.controller?.abort(); };
      for (const event of ['pointerover', 'pointerdown', 'focusin']) {
        this.addEventListener(event, this.onInteraction, { once: true });
      }
      this.onLoad = () => {
        if ('requestIdleCallback' in window) {
          this.idle = window.requestIdleCallback(() => this.randomize(), { timeout: 1000 });
        } else {
          this.startTimer = setTimeout(() => this.randomize(), 0);
        }
      };
      if (document.readyState === 'complete') this.onLoad();
      else window.addEventListener('load', this.onLoad, { once: true });
    }

    disconnectedCallback() {
      this.controller?.abort();
      window.removeEventListener('load', this.onLoad);
      if (this.idle !== undefined) window.cancelIdleCallback?.(this.idle);
      clearTimeout(this.startTimer);
      for (const event of ['pointerover', 'pointerdown', 'focusin']) {
        this.removeEventListener(event, this.onInteraction);
      }
    }

    async randomize() {
      if (this.started || this.interacted || !this.isConnected) return;
      this.started = true;
      const current = [...this.querySelectorAll(':scope > .product-card')];
      // Shopify permits pagination up to the 25,000th product.
      const total = Math.min(25000, Math.floor(Number(this.dataset.productCount)));
      const count = Math.min(current.length, total);
      if (!Number.isFinite(total) || count < 2 || total < current.length) return;
      const indices = sampleIndices(total, count);
      const key = `apex-featured:${this.dataset.collectionUrl}:${total}:${count}`;
      try {
        const previous = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (total > count && Array.isArray(previous) && indices.every(index => previous.includes(index))) {
          let replacement = Math.floor(Math.random() * total);
          while (indices.includes(replacement)) replacement = (replacement + 1) % total;
          indices[0] = replacement;
        }
      } catch (_) { /* Storage is optional, including in private browsing. */ }

      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 5000);
      try {
        const cards = await Promise.all(indices.map(async index => {
          if (index < current.length) return current[index];
          const url = new URL(this.dataset.collectionUrl, window.location.origin);
          if (url.origin !== window.location.origin) throw new Error('Invalid collection');
          const root = window.Shopify?.routes?.root || '/';
          if (!url.pathname.startsWith(root)) url.pathname = root.replace(/\/$/, '') + url.pathname;
          url.searchParams.set('section_id', 'featured-design-card');
          url.searchParams.set('page', String(index + 1));
          const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
          if (preview) url.searchParams.set('preview_theme_id', preview);
          const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Design unavailable');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const result = doc.querySelector('[data-featured-card]');
          const card = result?.querySelector('.product-card');
          if (!card || Number(result.dataset.index) !== index) throw new Error('Catalog changed');
          return card;
        }));
        const links = cards.map(card => card.querySelector('[data-card-product-link]')?.getAttribute('href'));
        if (links.some(link => !link) || new Set(links).size !== count) throw new Error('Duplicate designs');
        if (this.interacted || !this.isConnected || this.controller.signal.aborted) return;
        // Commit the complete set together; errors leave all original cards usable.
        this.replaceChildren(...cards);
        this.dataset.randomized = 'true';
        try { sessionStorage.setItem(key, JSON.stringify(indices)); } catch (_) { /* Optional. */ }
      } catch (_) {
        this.controller.abort();
      } finally {
        clearTimeout(timeout);
      }
    }
  }

  if (!customElements.get('apex-featured-designs')) customElements.define('apex-featured-designs', ApexFeaturedDesigns);
})();
